import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Loader2, Lock, MessageSquare, ChevronDown } from "lucide-react";

const ARRANGEMENTS = [
  "Roses", "White Roses", "Hydrangeas", "Peonies", "Mixed Luxury",
  "Garden Style", "Signature Collection", "Designer's Choice", "Seasonal Selection",
];

function loadPaypalSub(clientId) {
  if (window.paypalSub) return Promise.resolve(window.paypalSub);
  if (window.__ppSubPromise) return window.__ppSubPromise;
  window.__ppSubPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription&currency=USD`;
    s.setAttribute("data-namespace", "paypalSub");
    s.onload = () => resolve(window.paypalSub);
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return window.__ppSubPromise;
}

const fieldCls =
  "w-full bg-[#0c0c0c] border border-[#b8966a]/20 text-[#f8f5f0] px-4 py-3 font-body text-sm placeholder:text-[#a19d98]/50 focus:outline-none focus:border-[#b8966a] transition-colors";

const selectCls = `${fieldCls} appearance-none pr-10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`;

function NativeSelect({ children, ...props }) {
  return (
    <div className="relative">
      <select className={selectCls} {...props}>{children}</select>
      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b8966a] pointer-events-none" />
    </div>
  );
}

export default function MembershipCheckout({ tiers, selectedTier, setSelectedTier }) {
  const [form, setForm] = useState({
    frequency: "", arrangement: "Designer's Choice", name: "", email: "",
    phone: "", company: "", address: "", notes: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [submitting, setSubmitting] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalContainerRef = useRef(null);
  const paypalButtonsRef = useRef(null);

  const tierCfg = useMemo(
    () => tiers.find((t) => t.key === selectedTier) || null,
    [tiers, selectedTier]
  );
  const isConsultation = tierCfg?.consultation;

  // Keep latest form/tier available to PayPal closures
  const stateRef = useRef({});
  stateRef.current = { form, selectedTier, tierCfg };

  // Reset frequency when tier changes (default to first valid frequency)
  useEffect(() => {
    if (tierCfg && !tierCfg.consultation) {
      const valid = tierCfg.frequencies.map((f) => f.key);
      setForm((f) => ({ ...f, frequency: valid.includes(f.frequency) ? f.frequency : valid[0] || "" }));
    }
  }, [tierCfg]);

  useEffect(() => {
    axios.get(`${API}/paypal/client-id`).then((res) => {
      if (res.data.configured) setPaypalClientId(res.data.client_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (paymentMethod === "paypal" && paypalClientId) {
      loadPaypalSub(paypalClientId).then(() => setPaypalReady(true)).catch(() => {
        toast.error("Could not load PayPal. Please try card checkout.");
      });
    }
  }, [paymentMethod, paypalClientId]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const { name, email, frequency } = stateRef.current.form;
    if (!selectedTier) return "Please select a membership tier.";
    if (!name.trim()) return "Please enter your name.";
    if (!email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) return "Please enter a valid email.";
    if (!frequency) return "Please choose a billing frequency.";
    return null;
  };

  const buildPayload = () => {
    const { form: f, selectedTier: tier } = stateRef.current;
    return {
      tier, frequency: f.frequency, arrangement: f.arrangement,
      name: f.name, email: f.email, phone: f.phone, company: f.company,
      address: f.address, notes: f.notes, origin_url: window.location.origin,
    };
  };

  const handleStripe = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/memberships/checkout/stripe`, buildPayload());
      if (res.data.url) window.location.href = res.data.url;
      else throw new Error("No checkout URL");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  // Render PayPal subscription buttons
  useEffect(() => {
    if (paymentMethod !== "paypal" || isConsultation || !paypalReady || !paypalContainerRef.current) return;
    if (!window.paypalSub) return;
    if (paypalButtonsRef.current) {
      try { paypalButtonsRef.current.close(); } catch { /* ignore */ }
      paypalButtonsRef.current = null;
    }
    paypalContainerRef.current.replaceChildren();
    const buttons = window.paypalSub.Buttons({
      style: { layout: "vertical", color: "gold", shape: "pill", label: "subscribe" },
      createSubscription: async () => {
        const err = validate();
        if (err) { toast.error(err); throw new Error(err); }
        const res = await axios.post(`${API}/memberships/paypal/create`, buildPayload());
        if (!res.data.subscription_id) throw new Error("Could not create subscription");
        return res.data.subscription_id;
      },
      onApprove: async (data) => {
        try {
          await axios.post(`${API}/memberships/paypal/${data.subscriptionID}/activate`);
        } catch { /* finalize on success page */ }
        window.location.href = `/memberships/success?paypal_subscription_id=${data.subscriptionID}`;
      },
      onError: (err) => {
        console.error(err);
        toast.error("PayPal could not process the subscription. Please try card checkout.");
      },
    });
    buttons.render(paypalContainerRef.current).catch(() => {});
    paypalButtonsRef.current = buttons;
    return () => {
      if (paypalButtonsRef.current) {
        try { paypalButtonsRef.current.close(); } catch { /* ignore */ }
        paypalButtonsRef.current = null;
      }
    };
  }, [paymentMethod, paypalReady, isConsultation, selectedTier, form.frequency]);

  return (
    <div
      id="membership-checkout"
      className="bg-[#161616] border border-[#b8966a]/20 p-8 md:p-12 scroll-mt-24"
      data-testid="membership-checkout"
    >
      <p className="overline mb-3">Begin Your Membership</p>
      <h3 className="font-heading text-2xl md:text-3xl text-[#f8f5f0] mb-8">
        Secure Your Recurring Florals
      </h3>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        {/* Tier */}
        <div>
          <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#b8966a] mb-2 block">Membership Tier</label>
          <NativeSelect
            value={selectedTier || ""}
            onChange={(e) => setSelectedTier(e.target.value)}
            data-testid="membership-tier-select"
          >
            <option value="" disabled>Select a tier</option>
            {tiers.map((t) => (
              <option key={t.key} value={t.key} className="bg-[#161616]">
                {t.name}{t.price ? ` — $${t.price.toFixed(0)}/mo` : " — Custom"}
              </option>
            ))}
          </NativeSelect>
        </div>

        {/* Frequency */}
        <div>
          <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#b8966a] mb-2 block">Billing Frequency</label>
          <NativeSelect
            value={form.frequency}
            onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
            disabled={!tierCfg || isConsultation}
            data-testid="membership-frequency-select"
          >
            <option value="" disabled>Select frequency</option>
            {(tierCfg?.frequencies || []).map((fr) => (
              <option key={fr.key} value={fr.key} className="bg-[#161616]">{fr.label}</option>
            ))}
          </NativeSelect>
        </div>
      </div>

      {isConsultation ? (
        <div className="text-center py-8 border border-[#b8966a]/20 bg-[#0c0c0c]" data-testid="consultation-block">
          <MessageSquare className="mx-auto text-[#b8966a] mb-4" size={28} />
          <p className="font-body text-[#a19d98] max-w-md mx-auto mb-6">
            The Corporate Floral Membership is tailored to your space and cadence. Let's design a custom program and pricing together.
          </p>
          <a
            href="/inquiries?subject=Corporate%20Floral%20Membership%20Consultation"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="request-consultation-btn"
          >
            Request a Consultation
          </a>
        </div>
      ) : (
        <>
          <div className="mb-5">
            <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#b8966a] mb-2 block">Arrangement Preference</label>
            <NativeSelect
              value={form.arrangement}
              onChange={(e) => setForm((f) => ({ ...f, arrangement: e.target.value }))}
              data-testid="membership-arrangement-select"
            >
              {ARRANGEMENTS.map((a) => (
                <option key={a} value={a} className="bg-[#161616]">{a}</option>
              ))}
            </NativeSelect>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-5">
            <input className={fieldCls} placeholder="Full Name *" value={form.name} onChange={update("name")} data-testid="membership-name-input" />
            <input className={fieldCls} placeholder="Email *" type="email" value={form.email} onChange={update("email")} data-testid="membership-email-input" />
            <input className={fieldCls} placeholder="Phone" value={form.phone} onChange={update("phone")} data-testid="membership-phone-input" />
            <input className={fieldCls} placeholder="Company (optional)" value={form.company} onChange={update("company")} data-testid="membership-company-input" />
          </div>
          <input className={`${fieldCls} mb-5`} placeholder="Delivery Address" value={form.address} onChange={update("address")} data-testid="membership-address-input" />
          <textarea className={`${fieldCls} mb-6 resize-none`} rows={3} placeholder="Notes (gate codes, preferences, vessel style…)" value={form.notes} onChange={update("notes")} data-testid="membership-notes-input" />

          {/* Payment method toggle */}
          <div className="flex gap-3 mb-6">
            {["stripe", "paypal"].map((m) => (
              <button
                key={m}
                onClick={() => setPaymentMethod(m)}
                className={`flex-1 py-3 font-sans text-[10px] tracking-[0.2em] uppercase border transition-all ${
                  paymentMethod === m
                    ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]"
                    : "bg-transparent border-[#b8966a]/20 text-[#a19d98] hover:border-[#b8966a]/50"
                }`}
                data-testid={`payment-method-${m}`}
              >
                {m === "stripe" ? "Card / Klarna / Afterpay" : "PayPal"}
              </button>
            ))}
          </div>

          {paymentMethod === "stripe" ? (
            <button
              onClick={handleStripe}
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="membership-stripe-submit"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <Lock size={14} />}
              {submitting ? "Redirecting…" : "Start Membership"}
            </button>
          ) : (
            <div ref={paypalContainerRef} data-testid="membership-paypal-container" className="min-h-[52px]" />
          )}

          <p className="flex items-center justify-center gap-2 mt-5 font-body text-xs text-[#a19d98]">
            <Lock size={12} className="text-[#b8966a]" />
            Recurring billing. Cancel anytime from your member portal.
          </p>
        </>
      )}
    </div>
  );
}
